import * as React from 'react';
import { Group, createStyles, ScrollArea, Text, Select, Pagination, Table as TableBase } from '@mantine/core';
import { flexRender, Table, RowData, FilterFn } from '@tanstack/react-table';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useFocusWithin } from '@mantine/hooks';

// Components
import { Spinner } from '../Spinner';
import { ColumnFilterEnumDataSet, ColumnFilterCellValues } from './components/ColumnFilter';
import { SearchInput } from '../SearchInput';
import { ClearAllFiltersButton } from './components/ClearAllFiltersButton';

// Types
import { ICommonTableProps } from './types';

declare module '@tanstack/table-core' {
    interface ColumnMeta<TData extends RowData, TValue> {
        filterDataSetKey?: string;
        searchable?: boolean;
    }
    interface FilterFns {
        fuzzy?: FilterFn<unknown>;
        multi?: FilterFn<unknown>;
    }
}

const useStyles = createStyles((theme) => ({
    header: {
        position: 'sticky',
        top: 0,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        transition: 'box-shadow 150ms ease',
        zIndex: 2,

        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[2]}`
        }
    },

    tableGroup: {
        marginLeft: `-${theme.spacing.lg}`,
        marginRight: `-${theme.spacing.lg}`
    },

    scrolled: {
        zIndex: 1,
        boxShadow: theme.shadows.sm
    },

    scrollBar: {
        width: '100%',
        height: '100%',
        // maxHeight: '70vh',
        '.mantine-ScrollArea-scrollbar': {
            zIndex: 2
        }
    },

    tableCaption: {
        marginTop: '3rem',
        marginBottom: '3rem'
    },

    cardTitleGroup: {
        marginLeft: `-${theme.spacing.lg}`,
        marginRight: `-${theme.spacing.lg}`,
        paddingBottom: theme.spacing.sm,
        paddingTop: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
        paddingLeft: theme.spacing.sm,
        borderBottom: '1px solid #dee2e6',
        background: '#f9fcff'
        // background: theme.colors.brand[2]
        // background: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background
    },

    searchInput: {
        input: {
            borderRadius: '2rem'
        }
    },

    invisible: {
        pointerEvents: 'none',
        opacity: 0
    },

    sortIcon: {
        position: 'absolute',
        top: '50%',
        marginTop: '-6px',
        marginLeft: '2px'
    },

    relative: {
        position: 'relative'
    },

    tableRowHighlight: {
        background: '#fffbf5',
        td: {
            fontWeight: 500
        }
    },
    altRowColor: {
        background: '#f9fcff'
    },
    noMargin: {
        marginLeft: 0,
        marginRight: 0
    },
    tableHeaderCell: {
        whiteSpace: 'nowrap'
    },
    tableHeaderCellFilterable: {
        position: 'relative',
        paddingRight: '48px !important'
    },
    searchable: {
        // opacity: 0.2,
        background: theme.colors.brand[0]
        // borderLeft: '1px solid #59b160',
        // borderRight: '1px solid #59b160 !important'
    },
    columnFilterButton: {
        // position: 'absolute',
        // right: '10px',
        // top: '50%',
        // marginTop: '-14px'
    },
    filterControls: {
        gap: '0'
    },
    globalFilter: {
        input: {
            borderRadius: '2rem 0 0 2rem'
        }
    },
    clearFiltersButton: {
        borderRadius: '0 2rem 2rem 0',
        borderLeft: 'none'
    },
    row: {
        'td:first-of-type, th:first-of-type': {
            paddingLeft: '20px'
        }
    },
    paginationMenu: {
        borderTop: '1px solid #dee2e6',
        padding: theme.spacing.sm
    },
    resultsPerPage: {
        gap: '12px'
    },
    paginationInput: {
        maxWidth: '5rem'
    },
    columnHeaderContent: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    }
}));

interface TableTemplateProps<T> extends ICommonTableProps<T> {
    table: Table<T>;
    globalFilter?: string;
    setGlobalFilter?: React.Dispatch<React.SetStateAction<string>>;
    rightControl?: React.ReactNode;
    noMargin?: boolean;
    searchable?: boolean;
    noHeader?: boolean;
    loading: boolean;
    enableColumnFilters?: boolean;
    searchDisabled?: boolean;
    showPagination?: boolean;
    activeFilters?: number;
    pageIndex?: number;
    placeholder?: string | React.ReactNode;
    highlightRow?: (args: any) => boolean;
}

export function TableTemplate<T>(props: TableTemplateProps<T>) {
    const { classes, cx } = useStyles();
    const { t } = useTranslation('components', { keyPrefix: 'tables.common' });
    const {
        table,
        globalFilter = '',
        searchDisabled,
        setGlobalFilter,
        showPagination,
        loading,
        pageIndex,
        enableColumnFilters,
        activeFilters,
        highlightRow
    } = props;
    const [scrolled, setScrolled] = React.useState(false);
    const { ref: SearchFieldRef, focused } = useFocusWithin<HTMLInputElement>();

    return (
        <>
            {props.rightControl || props.searchable ? (
                <Group
                    position="apart"
                    className={cx(classes.cardTitleGroup, {
                        [classes.noMargin]: props.noMargin
                    })}
                >
                    <Group className={classes.filterControls}>
                        {props.searchable && setGlobalFilter ? (
                            <SearchInput
                                ref={SearchFieldRef}
                                className={enableColumnFilters ? classes.globalFilter : ''}
                                value={globalFilter}
                                setter={setGlobalFilter}
                                disabled={searchDisabled}
                            />
                        ) : null}
                        {enableColumnFilters ? (
                            <ClearAllFiltersButton
                                filterCount={activeFilters}
                                className={classes.clearFiltersButton}
                                resetGlobalFilter={table.resetGlobalFilter}
                                resetColumnFilters={table.resetColumnFilters}
                            />
                        ) : null}
                    </Group>
                    <div>{props.rightControl ?? null}</div>
                </Group>
            ) : null}
            <Group
                className={cx(classes.tableGroup, {
                    [classes.noMargin]: props.noMargin
                })}
            >
                {loading ? (
                    <Spinner />
                ) : (
                    <ScrollArea.Autosize
                        className={classes.scrollBar}
                        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
                    >
                        <TableBase sx={{ width: '100%' }} verticalSpacing="xs" captionSide="bottom">
                            {!props.noHeader ? (
                                <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr className={classes.row} key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                return (
                                                    <th
                                                        key={header.id}
                                                        className={cx(classes.tableHeaderCell, {
                                                            [classes.tableHeaderCellFilterable]:
                                                                header.column.getCanFilter(),
                                                            [classes.searchable]:
                                                                // header.column.columnDef.meta?.searchable
                                                                header.column.columnDef.meta?.searchable && focused
                                                        })}
                                                        style={{
                                                            width: header.getSize() !== 0 ? header.getSize() : undefined
                                                        }}
                                                    >
                                                        <div className={classes.columnHeaderContent}>
                                                            {header.isPlaceholder ? null : (
                                                                <div
                                                                    style={{ position: 'relative', cursor: 'pointer' }}
                                                                    onClick={header.column.getToggleSortingHandler()}
                                                                >
                                                                    {flexRender(
                                                                        header.column.columnDef.header,
                                                                        header.getContext()
                                                                    )}
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <IconArrowDown
                                                                            className={classes.sortIcon}
                                                                            size={12}
                                                                            color="orange"
                                                                        />
                                                                    ) : (
                                                                        <IconArrowUp
                                                                            size={12}
                                                                            className={cx(classes.sortIcon, {
                                                                                [classes.invisible]:
                                                                                    !header.column.getIsSorted()
                                                                            })}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                            {header.column.getCanFilter() ? (
                                                                <>
                                                                    {header.column.columnDef.meta?.filterDataSetKey ? (
                                                                        <ColumnFilterEnumDataSet
                                                                            className={classes.columnFilterButton}
                                                                            column={header.column}
                                                                            table={table}
                                                                            dataSetKey={
                                                                                header.column.columnDef.meta
                                                                                    .filterDataSetKey
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <ColumnFilterCellValues
                                                                            className={classes.columnFilterButton}
                                                                            column={header.column}
                                                                            table={table}
                                                                        />
                                                                    )}
                                                                </>
                                                            ) : null}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>
                            ) : null}
                            {table.getRowModel().rows.length ? (
                                <tbody>
                                    {table.getRowModel().rows.map((row, index) => {
                                        return (
                                            <tr
                                                key={row.id}
                                                className={cx(classes.row, {
                                                    [classes.altRowColor]: index % 2,
                                                    [classes.tableRowHighlight]:
                                                        typeof highlightRow !== 'undefined'
                                                            ? highlightRow(row.original)
                                                            : false
                                                })}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        className={cx(classes.relative, {
                                                            [classes.searchable]:
                                                                cell.column.columnDef.meta?.searchable && focused
                                                        })}
                                                        style={{
                                                            width:
                                                                cell.column.getSize() !== 0
                                                                    ? cell.column.getSize()
                                                                    : undefined
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            ) : activeFilters ? (
                                <caption>
                                    <Text size="xl" inline className={classes.tableCaption}>
                                        There are no results for the enabled filters.
                                    </Text>
                                </caption>
                            ) : (
                                <caption>
                                    <Text size="xl" inline className={classes.tableCaption}>
                                        {props.placeholder ?? 'No data.'}
                                    </Text>
                                </caption>
                            )}
                        </TableBase>
                    </ScrollArea.Autosize>
                )}
            </Group>
            {showPagination ? (
                <Group className={classes.paginationMenu} position="apart">
                    <Group className={classes.resultsPerPage}>
                        <Select
                            className={classes.paginationInput}
                            value={table.getState().pagination.pageSize?.toString()}
                            onChange={(val) => {
                                table.setPageSize(Number(val));
                                table.setPageIndex(1);
                            }}
                            data={[
                                {
                                    value: '10',
                                    label: '10'
                                },
                                {
                                    value: '25',
                                    label: '25'
                                },
                                {
                                    value: '50',
                                    label: '50'
                                }
                            ]}
                        />
                        <Text size="sm">{t('resultsPerPage')}</Text>
                    </Group>
                    <Pagination
                        total={table.getPageCount() - 1}
                        defaultValue={0}
                        value={pageIndex}
                        onChange={(page: number) => {
                            table.setPageIndex(page);
                        }}
                    />
                </Group>
            ) : null}
        </>
    );
}
